// ===================================================================
// 🔧 FUNÇÕES UTILITÁRIAS DO SISTEMA INGRESSAIF
// ===================================================================

export const validateFields = (fieldsObj, customMessage = 'Preencha todos os campos obrigatórios') => {
  const hasEmpty = Object.values(fieldsObj).some(value => !value || value === '');
  if (hasEmpty) {
    return false;
  }
  return true;
};

export const formatDate = (dateString) => {
  try {
    if (!dateString) return 'Data não informada';
    
    // Se já está no formato brasileiro DD/MM/YYYY, retornar como está
    if (String(dateString).includes('/') && !String(dateString).includes('T')) {
      return dateString;
    }
    
    // Se está no formato YYYY-MM-DD, converter para brasileiro
    if (String(dateString).includes('-') && !String(dateString).includes('T')) {
      const [year, month, day] = String(dateString).split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Datas ISO: backend usa UTC (datetime.utcnow). Se não tiver Z, assumir UTC.
    let str = String(dateString).trim();
    if (str.includes('T') && !str.includes('Z') && !str.includes('+') && !str.includes('-', 10)) {
      str = str.replace(/(\.\d+)?$/, '$1Z');
    }
    const date = new Date(str);
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.log('⚠️ Erro ao formatar data:', dateString, error);
    return 'Data inválida';
  }
};

/** Data/hora da inscrição (Brasília), com segundos — ordem de inscrição / comprovação. */
export const formatRegistrationDateTime = (dateString) => {
  try {
    if (!dateString) return '—';
    let str = String(dateString).trim();
    if (str.includes('T') && !str.includes('Z') && !str.includes('+') && !str.match(/[+-]\d{2}:\d{2}$/)) {
      str = str.replace(/(\.\d+)?$/, '$1Z');
    }
    const date = new Date(str);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '—';
  }
};

/** Mascara CPF para relatórios: 123.456.789-00 -> 123.***.***-00 */
export const maskCpf = (cpf) => {
  if (!cpf || typeof cpf !== 'string') return '';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length < 11) return cpf;
  return `${digits.slice(0, 3)}.***.***-${digits.slice(-2)}`;
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

/**
 * Nome padronizado para PDF/impressão: «NOME DO RELATÓRIO NOME DO PROCESSO.pdf» (maiúsculas).
 * Se processName estiver vazio, usa só o rótulo do relatório.
 */
export const buildReportExportFilename = (reportLabel, processName, extension = 'pdf') => {
  const report = String(reportLabel || 'RELATÓRIO').trim();
  const proc = String(processName || '').trim();
  let base = proc ? `${report} ${proc}` : report;
  base = base.replace(/[<>:"/\\|?*\x00-\x1f]+/g, '');
  base = base.replace(/\s+/g, ' ').trim().toUpperCase() || 'RELATORIO';
  if (!extension) return base;
  const ext = String(extension).startsWith('.') ? extension : `.${extension}`;
  return `${base}${ext}`;
};

/**
 * Impressão / «Salvar como PDF» com sugestão de nome (Chrome e Edge usam document.title).
 */
export const printWithDocumentTitle = (suggestedTitle) => {
  const title = String(suggestedTitle || 'RELATORIO')
    .replace(/\.pdf$/i, '')
    .replace(/[<>:"/\\|?*\x00-\x1f]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase()
    .slice(0, 200) || 'RELATORIO';
  const originalTitle = document.title;
  const restore = () => {
    document.title = originalTitle;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  const mq = window.matchMedia?.('print');
  if (mq?.addEventListener) {
    const onChange = (ev) => {
      if (!ev.matches) {
        restore();
        mq.removeEventListener('change', onChange);
      }
    };
    mq.addEventListener('change', onChange);
  }
  document.title = title;
  window.setTimeout(() => window.print(), 0);
};

/** Download de Blob com nome fixo (evita UUID quando o navegador ignora download em blob:). */
export const downloadBlobFile = (blob, filename) => {
  const safeName = (filename || 'arquivo').trim() || 'arquivo';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 200);
};

export const exportToCSV = (data, filename) => {
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 200);
  }
};

/** Converte YYYY-MM-DD ou DD/MM/YYYY para Date em horário LOCAL (evita meia-noite UTC) */
export const parseDateAsLocal = (s) => {
  if (!s || !String(s).trim()) return null;
  const str = String(s).trim();
  if (str.includes('-')) {
    const [y, m, d] = str.split('-');
    if (y && m && d) return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  if (str.includes('/')) {
    const [d, m, y] = str.split('/');
    if (y && m && d) return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
  }
  return null;
};

export const isProcessActive = (process) => {
  return getProcessRegistrationStatus(process) === 'open';
};

/** Período de inscrição do processo: open | upcoming | closed | invalid */
export const getProcessRegistrationStatus = (process) => {
  const now = new Date();
  const startDate = parseDateAsLocal(process?.start_date);
  const endDate = parseDateAsLocal(process?.end_date);
  if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 'invalid';
  }
  endDate.setHours(23, 59, 59, 999);
  if (now < startDate) return 'upcoming';
  if (now > endDate) return 'closed';
  return 'open';
};

export const isResourcePeriodActive = (process, resourceType) => {
  const now = new Date();
  let startField, endField;
  
  switch (resourceType) {
    case 'exemption':
      startField = 'exemption_appeal_start';
      endField = 'exemption_appeal_end';
      break;
    case 'registration':
      startField = 'registration_appeal_start';
      endField = 'registration_appeal_end';
      break;
    case 'result':
      startField = 'result_appeal_start';
      endField = 'result_appeal_end';
      break;
    default:
      return false;
  }
  
  if (!process[startField] || !process[endField]) return false;
  
  const startDate = parseDateAsLocal(process[startField]);
  const endDate = parseDateAsLocal(process[endField]);
  if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;
  endDate.setHours(23, 59, 59, 999);
  
  return now >= startDate && now <= endDate;
};

export const getAvailableResourceTypes = (process, userApplications) => {
  const availableTypes = [];
  
  // Verificar recursos de isenção
  if (isResourcePeriodActive(process, 'exemption')) {
    const hasExemptionRejected = userApplications.some(app =>
      app.exemption_status === 'rejected' && app.exemption_reason
    );
    if (hasExemptionRejected) {
      availableTypes.push('exemption');
    }
  }
  
  // Verificar recursos de inscrição
  if (isResourcePeriodActive(process, 'registration')) {
    const hasRejectedAnalysis = userApplications.some(app =>
      app.status === 'rejected' && app.rejection_reason
    );
    if (hasRejectedAnalysis) {
      availableTypes.push('registration');
    }
  }
  
  // Verificar recursos de resultado
  if (isResourcePeriodActive(process, 'result')) {
    const hasRejectedAnalysis = userApplications.some(app =>
      app.status === 'rejected' && app.rejection_reason
    );
    if (hasRejectedAnalysis) {
      availableTypes.push('result');
    }
  }
  
  return availableTypes;
};

export const calculateWeightedAverage = (grades, courseSubjects) => {
  let totalWeight = 0;
  let weightedSum = 0;
  
  courseSubjects.forEach(subject => {
    const serieGrades = grades[subject.serie];
    if (!serieGrades) return;
    
    subject.disciplinas.forEach(disciplina => {
      const grade = serieGrades[disciplina.name];
      if (grade !== undefined && grade !== null && grade !== '' && !isNaN(parseFloat(grade))) {
        weightedSum += parseFloat(grade) * disciplina.weight;
        totalWeight += disciplina.weight;
      }
    });
  });
  
  if (totalWeight === 0) return 0;
  
  const serieAverage = weightedSum / totalWeight;
  return Math.round(serieAverage * 100) / 100;
};

export const hasUserPaidInProcess = (userId, processId, payments) => {
  return payments.some(payment => 
    payment.user_id === userId && 
    payment.process_id === processId && 
    (payment.status === 'paid' || payment.status === 'exempt')
  );
};

export const getUserPaymentStatus = (userId, processId, payments) => {
  const payment = payments.find(payment =>
    payment.user_id === userId && payment.process_id === processId
  );
  return payment ? payment.status : 'pending';
};

/** Nome do curso com unidade para o candidato (ex.: "Energia Fotovoltaica — João Pinheiro MG"). */
export const formatCourseDisplayLabel = (name, campus, { fallback = 'Curso não informado' } = {}) => {
  const n = String(name || '').trim();
  const c = String(campus || '').trim();
  if (!n && !c) return fallback;
  if (!c) return n || fallback;
  if (!n) return c;
  return `${n} — ${c}`;
};

export const formatCourseDisplayFromObject = (course, fallback = 'Curso não informado') =>
  formatCourseDisplayLabel(
    course?.name ?? course?.course_name,
    course?.campus ?? course?.course_campus,
    { fallback },
  );

/** Domínio base dos tenants (ex.: sistemaingressa.com.br). null = localhost dev. */
export const tenantBaseDomain = () => {
  const host = window.location.hostname;
  if (!host || host === 'localhost' || host.endsWith('.localhost')) return null;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return 'sistemaingressa.com.br';
  const dot = host.indexOf('.');
  return dot > 0 ? host.slice(dot + 1) : null;
};

/** URL de acesso ao ambiente de uma instituição (subdomínio). */
export const tenantAccessUrl = (slug) => {
  const s = (slug || 'slug').trim().toLowerCase();
  const base = tenantBaseDomain();
  if (!base) {
    const port = window.location.port || '5000';
    return `http://${s}.localhost:${port}`;
  }
  return `https://${s}.${base}`;
};

/** Host exibível (sem scheme) — ex.: vds.sistemaingressa.com.br */
export const tenantAccessHost = (slug) => tenantAccessUrl(slug).replace(/^https?:\/\//, '');